import {Button} from 'antd';
import dayjs from 'dayjs';
import html2pdf from 'html2pdf.js';
import PropTypes from 'prop-types';
import {useRef} from 'react';

const InvoiceScreen = ({
  selectedOrderSummary,
  setSelectedOrderSummary,
  setOpenInvoiceModal,
  categoryList = [],
}) => {
  const invoiceRef = useRef(null);

  if (!selectedOrderSummary) {
    return <div>No invoice data available.</div>;
  }

  const orderSummary = selectedOrderSummary;

  let totalTaxable = 0;
  let totalCgst = 0;
  let totalSgst = 0;
  let grandTotal = 0;

  const promoCode = orderSummary.appliedPromoCode || null;

  const promoDiscountPercent = Number(
    orderSummary.promoDiscountPercent ||
    orderSummary.appliedPromoCodePercentage || 
  orderSummary.orderPromoCodeInfo?.discountValue ||
  0,
  );

  const handleDownloadPDF = () => {
    html2pdf()
      .set({
        margin: 0.5,
        filename: `Invoice-${orderSummary.transactionId}.pdf`,
        image: {type: 'jpeg', quality: 0.98},
        html2canvas: {scale: 2},
        jsPDF: {unit: 'mm', format: 'a4', orientation: 'landscape'},
      })
      .from(invoiceRef.current)
      .save();

    setSelectedOrderSummary(null);
    setOpenInvoiceModal(false);
  };

  return (
    <div>
      <div className={'max-w-3xl mx-auto mb-6 flex justify-end'}>
        <Button type={'primary'} size={'large'} onClick={handleDownloadPDF}>
          Download Invoice
        </Button>
      </div>

      <div
        ref={invoiceRef}
        className={'bg-white ml-24 text-[12px] font-medium p-3'}
        style={{width: '250mm', minHeight: '180mm'}}
      >
        <h1 className={'text-3xl font-bold text-center mb-2'}>INVOICE</h1>

        <div className={'grid grid-cols-2 mb-2'}>
          <div className={'p-2 font-semibold'}>
            Date: {dayjs(orderSummary.createdOn).format('DD-MMM-YYYY')}
          </div>
        </div>

        <div className={'grid grid-cols-2 border mb-4'}>
          <div className={'p-2 border-r'}>
            <h2 className={'font-bold text-base'}>Kalapi Store</h2>
            <p>1553, C WARD ROYAL ARCADE</p>
            <p>Kolhapur - 416002</p>
            <p>Mob: 09422051275</p>
          </div>

          <div className={'p-2'}>
            <p className={'font-bold text-base'}>Customer</p>
            <p>
              {orderSummary.userInfo.firstName}{' '}
              {orderSummary.userInfo.lastName}
            </p>
            <p>
              {[
                orderSummary.userInfo.apartment,
                orderSummary.userInfo.street,
                orderSummary.userInfo.city,
                orderSummary.userInfo.district,
                orderSummary.userInfo.state,
                orderSummary.userInfo.pinCode,
              ]
                .filter(Boolean)
                .join(', ')}
            </p>
            <p>Mobile: {orderSummary.userInfo.mobile}</p>
            <p>Email: {orderSummary.userInfo.email}</p>

            {orderSummary.businessName && (
              <p>Business Name: {orderSummary.businessName}</p>
            )}

            {orderSummary.gstIn && <p>GSTIN: {orderSummary.gstIn}</p>}
          </div>
        </div>

        <table className={'border-collapse border w-full text-sm'}>
          <thead>
            <tr className={'bg-gray-200 font-bold'}>
              <th className={'border p-1'}>Sr</th>
              <th className={'border p-1'}>Product</th>
              <th className={'border p-1'}>HSN</th>
              <th className={'border p-1'}>Qty</th>
              <th className={'border p-1'}>Unit Price</th>
              <th className={'border p-1'}>Taxable</th>
              <th className={'border p-1'}>CGST</th>
              <th className={'border p-1'}>SGST</th>
              <th className={'border p-1'}>Total</th>
            </tr>
          </thead>

          <tbody>
            {orderSummary.products.map((item, idx) => {
              const basePrice = Number(item.productInfo.price || 0);
              const productDiscountPercent = Number(
                item.productInfo.discount || 0,
              );

              const productDiscountAmount =
                productDiscountPercent > 0
                  ? (basePrice * productDiscountPercent) / 100
                  : 0;

              const sizeExtra =
                item.productInfo.variants
                  ?.find(v => v.color === item.color)
                  ?.sizes?.find(s => s.size === item.size)
                  ?.extraPrice || 0;

              const unitPrice =
                basePrice - productDiscountAmount + Number(sizeExtra);

              const totalPrice = unitPrice * item.quantity;

              const matchedCategory = Array.isArray(categoryList)
                ? categoryList.find(
                  c => c.name === item.productInfo.category,
                )
                : null;

              const hsnCode = matchedCategory?.tax?.hsnCode || '';
              const cgstRate = Number(matchedCategory?.tax?.cgst || 0);
              const sgstRate = Number(matchedCategory?.tax?.sgst || 0);
              const totalGstRate = cgstRate + sgstRate;

              const gstAmount =
                totalGstRate > 0
                  ? (totalPrice * totalGstRate) / (100 + totalGstRate)
                  : 0;

              const taxableValue = totalPrice - gstAmount;
              const cgst =
                totalGstRate > 0
                  ? (gstAmount * cgstRate) / totalGstRate
                  : 0;
              const sgst =
                totalGstRate > 0
                  ? (gstAmount * sgstRate) / totalGstRate
                  : 0;

              totalTaxable += taxableValue;
              totalCgst += cgst;
              totalSgst += sgst;
              grandTotal += totalPrice;

              return (
                <tr key={idx}>
                  <td className={'border text-center'}>{idx + 1}</td>
                  <td className={'border p-1'}>
                    {item.productInfo.productName}
                  </td>
                  <td className={'border text-center'}>{hsnCode}</td>
                  <td className={'border text-center'}>{item.quantity}</td>
                  <td className={'border text-right p-1'}>
                    {unitPrice.toFixed(2)}
                  </td>
                  <td className={'border text-right p-1'}>
                    {taxableValue.toFixed(2)}
                  </td>
                  <td className={'border text-right p-1'}>
                    {cgst.toFixed(2)} ({cgstRate}%)
                  </td>
                  <td className={'border text-right p-1'}>
                    {sgst.toFixed(2)} ({sgstRate}%)
                  </td>
                  <td className={'border text-right p-1'}>
                    {totalPrice.toFixed(2)}
                  </td>
                </tr>
              );
            })}

            <tr className={'font-bold'}>
              <td className={'border text-center'} colSpan={5}>
                Sub Total
              </td>
              <td className={'border text-right p-1'}>
                {totalTaxable.toFixed(2)}
              </td>
              <td className={'border text-right p-1'}>
                {totalCgst.toFixed(2)}
              </td>
              <td className={'border text-right p-1'}>
                {totalSgst.toFixed(2)}
              </td>
              <td className={'border text-right p-1'}>
                {grandTotal.toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>

        {/*  NET AMOUNT WITH PROMO  */}
        {(() => {
          const totalPromoDiscount =
  orderSummary.promoDiscountAmount ??
  (promoDiscountPercent > 0
    ? (grandTotal * promoDiscountPercent) / 100
    : 0);

          const netAmount = grandTotal - totalPromoDiscount;

          return (
            <div className={'grid grid-cols-2 border mt-3'}>
              <div className={'p-2 border-r'}>
                <b>Declaration:</b> We declare that this invoice shows the actual
                price of goods described.
              </div>

              <div className={'p-2 text-lg text-right'}>
                <p className={'font-bold'}>Gross Amount</p>
                <p className={'text-base font-semibold'}>
                  ₹ {grandTotal.toFixed(2)}
                </p>

                {promoCode && (
                  <p className={'text-base font-semibold'}>
     Applied Promocode: {promoCode} ({promoDiscountPercent}% off) - ₹
                    {totalPromoDiscount.toFixed(2)}
                  </p>
                )}
                <p className={'text-xl font-bold mt-2'}>
                  Net Amount: ₹ {Math.round(netAmount.toFixed(2))}
                </p>
              </div>
            </div>
          );
        })()}
      </div>
      <p className={'text-center text-gray font-md text-base'}><i>- Thanks for shopping with us -</i></p>
    </div>
  );
};

InvoiceScreen.propTypes = {
  selectedOrderSummary: PropTypes.object,
  setSelectedOrderSummary: PropTypes.func,
  setOpenInvoiceModal: PropTypes.func,
  categoryList: PropTypes.array,
};

export default InvoiceScreen;
